import { createContext, useContext } from 'react';

export type WizardNavigationContextValue = {
  goToStep: (stepKey: string) => void;
  currentStepKey: string;
  reportError: () => void;
  clearError: () => void;
};

const WizardNavigationContext = createContext<WizardNavigationContextValue | null>(null);

export function WizardNavigationProvider({
  children,
  value,
}: {
  children: React.ReactNode;
  value: WizardNavigationContextValue;
}) {
  return <WizardNavigationContext.Provider value={value}>{children}</WizardNavigationContext.Provider>;
}

/**
 * Wizard navigation when the caller may render outside `WizardNavigationProvider`
 * (e.g. shared PostingPath fields). Returns `null` outside the provider.
 *
 * Example: `PhotoGallery` on `/properties/[listingAlias]/listing` mounts under
 * `PropertyDetailsContainer` → `MFAccordion` → `MediaContent` → `PhotoUploadFormField`,
 * not under `CreateListingPage`, so `useIsOnReviewStep` (which uses this hook) correctly
 * sees `null` and uses listing photo APIs instead of multiplex session APIs.
 */
export function useOptionalWizardNavigation(): WizardNavigationContextValue | null {
  return useContext(WizardNavigationContext);
}

/**
 * Wizard navigation for create-listing wizard UI only. Throws outside
 * `WizardNavigationProvider`; prefer `useOptionalWizardNavigation` when optional.
 */
export function useWizardNavigation() {
  const context = useOptionalWizardNavigation();
  if (!context) {
    throw new Error('useWizardNavigation must be used within WizardNavigationProvider');
  }
  return context;
}
